import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import {
    Icon,
    IconColor,
    IconID,
    IconSize,
} from '@thoughtspot/radiant-react/widgets/icon';
import { isSpreadsheetsEnabled } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '/@contexts/global-app-context';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import { getComputedNavThemeType } from '../../components/nav/navigation/left-navigation.util';
import { LeftNavigationOverlayHub } from '../../components/nav/navigation/left-navigation-overlay-hub.container';
import styles from './search-data-layout.module.scss';

type ViewMode = 'query' | 'spreadsheet';

interface SearchDataHeaderProps {
    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
}

const SearchDataHeader: React.FC<SearchDataHeaderProps> = React.memo(
    ({ viewMode, onViewModeChange }) => {
        const [
            showLeftNavigationOverlayHub,
            setShowLeftNavigationOverlayHub,
        ] = React.useState(false);

        const {
            customStylingService,
            sessionService,
            metricsService,
        } = useAppContext();
        const themeType = useMemo(
            () =>
                getComputedNavThemeType(customStylingService.getConfig(), true),
            [customStylingService],
        );

        const hamburgerIconHoverHandlerDebounce = useMemo(
            () =>
                _.debounce((value: boolean) => {
                    if (value) return;
                    const el = document.querySelector('.popover-module__in');
                    if (el) return;
                    setShowLeftNavigationOverlayHub(value);
                }, 100),
            [],
        );

        const hamburgerIconHoverHandler = useCallback(
            (
                value: boolean,
                fromHamburgerIcon = false,
                forceToClose = false,
            ) => {
                if (fromHamburgerIcon) {
                    (document.activeElement as HTMLElement | null)?.blur?.();
                    document.dispatchEvent(new MouseEvent('mousedown'));
                    setShowLeftNavigationOverlayHub(true);
                    return;
                }
                if (forceToClose) {
                    setShowLeftNavigationOverlayHub(false);
                    return;
                }
                hamburgerIconHoverHandlerDebounce(value);
            },
            [hamburgerIconHoverHandlerDebounce],
        );

        const handleHamburgerMouseOver = useCallback(() => {
            if (!sessionService.isHambugerHidden()) {
                hamburgerIconHoverHandler(true, true);
            }
        }, [sessionService, hamburgerIconHoverHandler]);

        const handleHamburgerClick = useCallback(() => {
            metricsService.collectEvent(
                new UserAction(userActionTypeEnum.HAMBURGER_ICON_CLICK),
                {},
            );
        }, [metricsService]);

        const handleQueryClick = useCallback(() => onViewModeChange('query'), [
            onViewModeChange,
        ]);

        const handleSpreadsheetClick = useCallback(
            () => onViewModeChange('spreadsheet'),
            [onViewModeChange],
        );

        return (
            <>
                <div className={styles.header}>
                    <button
                        type="button"
                        data-testid="hamburger-icon"
                        className={styles.hamburgerIconContainer}
                        onMouseOver={handleHamburgerMouseOver}
                        onFocus={() => null} // No-op fn to disable lints, on focus is not provided to match with existing functionality
                        onClick={handleHamburgerClick}
                    >
                        <Icon
                            className={styles.hamburgerIcon}
                            id={IconID.HAMBURGER}
                            color={IconColor.BLACK}
                            size={IconSize.LARGE}
                        />
                    </button>
                    <h1 className={styles.title}>Search data</h1>
                    <div className={styles.toggle}>
                        <button
                            className={`${styles.toggleBtn} ${
                                viewMode === 'query' ? styles.active : ''
                            }`}
                            type="button"
                            onClick={handleQueryClick}
                        >
                            Query
                        </button>
                        <button
                            className={`${styles.toggleBtn} ${
                                viewMode === 'spreadsheet' ? styles.active : ''
                            }`}
                            type="button"
                            onClick={handleSpreadsheetClick}
                        >
                            Spreadsheet
                        </button>
                    </div>
                </div>
                <LeftNavigationOverlayHub
                    hamburgerIconHoverHandler={hamburgerIconHoverHandler}
                    showLeftNavigationOverlayHub={showLeftNavigationOverlayHub}
                    themeType={themeType}
                />
            </>
        );
    },
);

export const SearchDataLayout: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const spreadsheetsEnabled = isSpreadsheetsEnabled();

    const isSpreadsheet = location.pathname.endsWith('/spreadsheet');
    const viewMode: ViewMode = isSpreadsheet ? 'spreadsheet' : 'query';

    const handleViewModeChange = useCallback(
        (mode: ViewMode) => {
            if (mode === 'spreadsheet' && !isSpreadsheet) {
                navigate(`${location.pathname}/spreadsheet`);
            } else if (mode === 'query' && isSpreadsheet) {
                navigate(location.pathname.replace(/\/spreadsheet$/, ''));
            }
        },
        [navigate, location.pathname, isSpreadsheet],
    );

    if (spreadsheetsEnabled && !isEmbeddedApp()) {
        return (
            <div className={styles.searchDataLayout}>
                <SearchDataHeader
                    viewMode={viewMode}
                    onViewModeChange={handleViewModeChange}
                />
                <div className={styles.queryContent}>
                    <Outlet />
                </div>
            </div>
        );
    }
    return <Outlet />;
};
